import { t } from '@hefe/i18n';
import {
  BasketballEvent,
  BasketballEventReplyAnswer,
  BasketballEventType,
  BasketballEventUserRolePlayerCoach,
} from '@hefe/models';
import { BasketballEventsSlice, EventsApi } from '@hefe/store';
import { colors } from '@hefe/ui';
import {
  Avatar,
  Col,
  Input,
  message,
  Modal,
  Popover,
  Row,
  Space,
  Spin,
  Typography,
} from 'antd';
import { useState } from 'react';
import { FaCheck, FaQuestion, FaTimes, FaUserSlash } from 'react-icons/fa';
import { useAppDispatch } from '../../../../store/store';

export interface EventReplyComponentProps {
  event: BasketballEvent;
  eventRole: BasketballEventUserRolePlayerCoach;
  userId: number;
  size: 'small' | 'default' | 'large';
}

export default function EventReplyComponent(props: EventReplyComponentProps) {
  const { event, eventRole, userId } = props;

  const dispatch = useAppDispatch();

  const [popupOpen, setPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reasonText, setReasonText] = useState('');

  const [updateEventReply, { isLoading: isUpdatingEventReply }] =
    EventsApi.useUpdateEventReplyMutation();

  let Icon, backgroundColor;
  switch (eventRole.reply?.reply) {
    case BasketballEventReplyAnswer.COMMITMENT:
      Icon = FaCheck;
      backgroundColor = colors.green;
      break;
    case BasketballEventReplyAnswer.CANCELED:
      Icon = FaTimes;
      backgroundColor = colors.red;
      break;
    default:
      Icon = FaQuestion;
      backgroundColor = colors.grey;
      break;
  }

  if (!eventRole.nominated) {
    Icon = FaUserSlash;
    backgroundColor = colors.secondary;
  }

  const onClickEventReply = (reply: number) => {
    const openRequireDialog =
      eventRole.reply?.require_reply_reason &&
      reply === BasketballEventReplyAnswer.CANCELED;
    if (openRequireDialog) {
      setReasonModalOpen(true);
    } else {
      sendReply(event.type, reply);
    }
  };

  const sendReply = (
    type: BasketballEventType,
    reply: number,
    reason?: string
  ) => {
    if (type === BasketballEventType.GAME) {
      replyToGame(reply, reason);
    } else if (type === BasketballEventType.PRACTICE) {
      replyToPractice(reply, reason);
    } else if (type === BasketballEventType.EVENT) {
      replyToEvent(reply, reason);
    }
  };

  const replyToGame = (reply: number, reason?: string) => {
    setLoading(true);
    setPopupOpen(false);
    dispatch(
      BasketballEventsSlice.updateUserBasketballGameReply({
        game_id: event.id,
        team_id: eventRole.team_id ?? 0,
        user_id: userId,
        reply: reply,
        reason: reason,
      })
    )
      .unwrap()
      .finally(() => {
        setLoading(false);
        setReasonText('');
      });
  };

  const replyToPractice = (reply: number, reason?: string) => {
    setLoading(true);
    setPopupOpen(false);
    dispatch(
      BasketballEventsSlice.updateUserBasketballPracticeReply({
        practice_id: event.id,
        team_id: eventRole.team_id ?? 0,
        user_id: userId,
        reply: reply,
        reason: reason,
      })
    )
      .unwrap()
      .finally(() => {
        setLoading(false);
        setReasonText('');
      });
  };

  const replyToEvent = (reply: number, reason?: string) => {
    setLoading(true);
    setPopupOpen(false);
    updateEventReply({
      eventId: event.id,
      request: {
        user_id: userId,
        reply: reply,
        reason: reason,
      },
    })
      .unwrap()
      .finally(() => {
        setLoading(false);
        setReasonText('');
      });
  };

  const renderReplyContent = () => {
    if (loading) {
      return (
        <Row align="middle" justify="center">
          <Col>
            <Spin />
          </Col>
        </Row>
      );
    }

    return (
      <Row align="middle" justify="space-around">
        <Col
          style={{ cursor: 'pointer' }}
          onClick={() =>
            onClickEventReply(BasketballEventReplyAnswer.COMMITMENT)
          }
        >
          <Avatar
            icon={<FaCheck style={{ marginTop: 5 }} />}
            style={{ backgroundColor: colors.green }}
          />
        </Col>
        <Col
          style={{ cursor: 'pointer' }}
          onClick={() => onClickEventReply(BasketballEventReplyAnswer.CANCELED)}
        >
          <Avatar
            icon={<FaTimes style={{ marginTop: 5 }} />}
            style={{ backgroundColor: colors.red }}
          />
        </Col>
      </Row>
    );
  };

  /**********************************************************************
   *                      MAIN RENDER
   **********************************************************************
   */
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Popover
        open={popupOpen}
        onOpenChange={(event) => {
          if (eventRole.nominated) {
            setPopupOpen(!popupOpen);
          } else {
            message.warning(t('nominees.user_not_nominated'));
          }
        }}
        trigger="click"
        title={t('change_answer')}
        content={renderReplyContent()}
      >
        {loading ? (
          <Avatar icon={<Spin size="small" />} />
        ) : (
          <Avatar
            icon={
              <Icon style={{ marginTop: props.size === 'small' ? 3 : 8 }} />
            }
            style={{ backgroundColor }}
          />
        )}
      </Popover>
      <Modal
        open={reasonModalOpen}
        title={t('reason')}
        onCancel={() => setReasonModalOpen(false)}
        okButtonProps={{
          disabled: reasonText.length === 0,
        }}
        onOk={() => {
          sendReply(
            event.type,
            BasketballEventReplyAnswer.CANCELED,
            reasonText
          );
          setReasonModalOpen(false);
        }}
      >
        <Space direction="vertical">
          <Typography.Text>
            {t('information_texts.reply_reason')}
          </Typography.Text>
          <Input
            value={reasonText}
            onChange={(event) => setReasonText(event.target.value)}
          />
        </Space>
      </Modal>
    </div>
  );
}
